import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api/apiClient';

const RarCounter = () => {
  const [rarRequests, setRarRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [packageFilter, setPackageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch RAR requests from API
  const fetchRarRequests = async () => {
    try {
      const client = getApiClient();
      
      // Real RAR API endpoint for VIN requests
      const response = await client.get('/admin/rar/vinrequests');

      // Package pricing mapping
      const packages = {
        'PACKAGE1': { name: 'Pachetul 1', price: 1.06, description: 'valabilitate ITP' },
        'PACKAGE2': { name: 'Pachetul 2', price: 2.12, description: 'date din CIV' },
        'PACKAGE3': { name: 'Pachetul 3', price: 3.17, description: 'pachetul 1 + pachetul 2' },
        'PACKAGE4': { name: 'Pachetul 4', price: 4.23, description: 'pachetul 3 + istoric km' }
      };

      // Transform API response data
      const transformedData = response.data.map((item) => {
        const packageInfo = packages[item.packageField] || packages['PACKAGE1'];
        return {
          id: item.id,
          packageType: packageInfo.name,
          vin: item.vin,
          status: 'success', // Assuming successful if returned from API
          timestamp: item.createdAtUtc,
          responseTime: item.responseTimeMs,
          userId: null, // Not provided in API response
          package: packageInfo.name,
          cost: packageInfo.price,
          description: packageInfo.description,
          packageField: item.packageField
        };
      });

      // Sort by timestamp (most recent first)
      setRarRequests(transformedData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      setError('');
    } catch (err) {
      console.error('Error fetching RAR requests:', err);
      setError('Failed to fetch RAR requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchRarRequests();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter RAR requests based on selected filters
  const filteredRarRequests = rarRequests.filter(request => {
    const requestDate = new Date(request.timestamp);
    const requestMonth = `${requestDate.getFullYear()}-${String(requestDate.getMonth() + 1).padStart(2, '0')}`;
    
    const monthMatch = monthFilter === 'all' || requestMonth === monthFilter;
    const packageMatch = packageFilter === 'all' || request.packageType === packageFilter;
    const statusMatch = statusFilter === 'all' || request.status === statusFilter;
    
    return monthMatch && packageMatch && statusMatch;
  });

  // Calculate statistics for filtered data
  const getStatistics = () => {
    const total = filteredRarRequests.length;
    const successful = filteredRarRequests.filter(req => req.status === 'success').length;
    const failed = filteredRarRequests.filter(req => req.status === 'failed').length;
    const pending = filteredRarRequests.filter(req => req.status === 'pending').length;
    const totalCost = filteredRarRequests.reduce((sum, req) => sum + req.cost, 0);
    const avgResponseTime = filteredRarRequests.length > 0 
      ? Math.round(filteredRarRequests.reduce((sum, req) => sum + req.responseTime, 0) / filteredRarRequests.length)
      : 0;

    return { total, successful, failed, pending, totalCost: totalCost.toFixed(2), avgResponseTime };
  };

  // Get unique months from requests for filter dropdown
  const getAvailableMonths = () => {
    const months = new Set();
    rarRequests.forEach(request => {
      const date = new Date(request.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  };

  // Get unique package types for filter dropdown
  const getAvailablePackages = () => {
    const packages = new Set();
    rarRequests.forEach(request => {
      packages.add(request.packageType);
    });
    return Array.from(packages).sort();
  };

  // Format month for display
  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const stats = getStatistics();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-gray-600">Loading RAR requests...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards - horizontally scrollable on mobile */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max md:grid md:grid-cols-3 lg:grid-cols-6 md:min-w-0">
          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">
                      {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') ? 'Filtered Requests' : 'Total Requests'}
                    </dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Successful</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.successful}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Failed</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.failed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.pending}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">
                      {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') ? 'Filtered Cost' : 'Total Cost'}
                    </dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.totalCost} RON</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Avg Response</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{stats.avgResponseTime}ms</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* RAR Requests Table (hidden on mobile) */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md hidden md:block">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">RAR API Requests</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Recent requests to RAR services with status and response times
                </p>
              </div>
              {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  onClick={() => {
                    setMonthFilter('all');
                    setPackageFilter('all');
                    setStatusFilter('all');
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            {/* Filter Controls in Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="month-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  id="month-filter"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Months</option>
                  {getAvailableMonths().map(month => (
                    <option key={month} value={month}>{formatMonth(month)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="package-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Package Type
                </label>
                <select
                  id="package-filter"
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Packages</option>
                  {getAvailablePackages().map(pkg => (
                    <option key={pkg} value={pkg}>{pkg}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            
            {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') && (
              <div className="mt-3">
                <span className="text-sm text-gray-600">
                  Showing {filteredRarRequests.length} of {rarRequests.length} requests
                </span>
              </div>
            )}
          </div>

          {filteredRarRequests.length === 0 ? (
            <div className="p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No RAR requests</h3>
              <p className="mt-1 text-sm text-gray-500">
                {rarRequests.length === 0 ? 'No RAR requests found.' : 'No requests match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VIN
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost (RON)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRarRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                            request.packageType === 'Pachetul 1' ? 'bg-blue-100 text-blue-800' :
                            request.packageType === 'Pachetul 2' ? 'bg-green-100 text-green-800' :
                            request.packageType === 'Pachetul 3' ? 'bg-purple-100 text-purple-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {request.packageType}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">{request.description}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">
                          {request.vin}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          request.status === 'success' ? 'bg-green-100 text-green-800' :
                          request.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{request.responseTime}ms</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{request.cost.toFixed(2)} RON</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatDate(request.timestamp)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Card Layout (only on mobile) */}
        <div className="md:hidden">
          {/* Mobile Filter Controls */}
          <div className="bg-white shadow rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Filters</h3>
              {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  onClick={() => {
                    setMonthFilter('all');
                    setPackageFilter('all');
                    setStatusFilter('all');
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="block w-full border pl-3 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Months</option>
                  {getAvailableMonths().map(month => (
                    <option key={month} value={month}>{formatMonth(month)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="mobile-package-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  Package Type
                </label>
                <select
                  id="mobile-package-filter"
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  className="block w-full border pl-3 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Packages</option>
                  {getAvailablePackages().map(pkg => (
                    <option key={pkg} value={pkg}>{pkg}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="mobile-status-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="mobile-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full pl-3 border pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            {(monthFilter !== 'all' || packageFilter !== 'all' || statusFilter !== 'all') && (
              <div className="mt-3">
                <span className="text-xs text-gray-600">
                  Showing {filteredRarRequests.length} of {rarRequests.length} requests
                </span>
              </div>
            )}
          </div>

          {filteredRarRequests.length === 0 ? (
            <div className="p-6 text-center bg-white rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No RAR requests</h3>
              <p className="mt-1 text-sm text-gray-500">
                {rarRequests.length === 0 ? 'No RAR requests found.' : 'No requests match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRarRequests.map((request) => (
                <div key={request.id} className="bg-white rounded-lg shadow p-4 flex flex-col space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      request.packageType === 'Pachetul 1' ? 'bg-blue-100 text-blue-800' :
                      request.packageType === 'Pachetul 2' ? 'bg-green-100 text-green-800' :
                      request.packageType === 'Pachetul 3' ? 'bg-purple-100 text-purple-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {request.packageType}
                    </span>
                    <span className="font-mono text-sm text-gray-900">{request.vin}</span>
                  </div>
                  <div className="text-xs text-gray-500">{request.description}</div>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      request.status === 'success' ? 'bg-green-100 text-green-800' :
                      request.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {request.status}
                    </span>
                    <span className="text-sm text-gray-500">{request.responseTime}ms</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Cost: {request.cost.toFixed(2)} RON</span>
                    <span className="text-xs text-gray-400">{formatDate(request.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Information Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About RAR API Requests</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  This page displays all requests made to RAR (Romanian Auto Register) services. 
                  You can monitor request types (ITP, RCA, CASCO, Registration), success rates, 
                  response times, and associated costs in credits.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RAR Pricing Information */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">RAR Package Pricing</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Official pricing for RAR API packages (excluding VAT)
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              <div className="border-l-4 border-blue-400 pl-4">
                <h4 className="text-sm font-semibold text-gray-900">Pachetul 1: valabilitate ITP</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Prin interogarea după numărul de identificare se obține data până la care este valabilă ultima ITP.
                </p>
                <p className="mt-2 text-sm font-medium text-blue-600">
                  Tariful perceput este de 1,06 lei fără TVA/interogare
                </p>
              </div>

              <div className="border-l-4 border-green-400 pl-4">
                <h4 className="text-sm font-semibold text-gray-900">Pachetul 2: date din CIV</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Prin interogarea după numărul de identificare se obțin caracteristicile tehnice cuprinse în CIV.
                </p>
                <p className="mt-2 text-sm font-medium text-green-600">
                  Tariful perceput este de 2,12 lei fără TVA/interogare
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="text-sm font-semibold text-gray-900">Pachetul 3: pachetul 1 + pachetul 2</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Combinația dintre Pachetul 1 și Pachetul 2.
                </p>
                <p className="mt-2 text-sm font-medium text-purple-600">
                  Tariful cumulat este de 3,17 lei fără TVA/interogare
                </p>
              </div>

              <div className="border-l-4 border-orange-400 pl-4">
                <h4 className="text-sm font-semibold text-gray-900">Pachetul 4: pachetul 3 + istoric km</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Istoricul de km cuprinde citirile odometrului realizate la reprezentanțele RAR și stațiile ITP 
                  începând cu 01.01.2014.
                </p>
                <p className="mt-2 text-sm font-medium text-orange-600">
                  Tariful perceput este de 4,23 lei fără TVA/interogare
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RarCounter;
